import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '@/lib/api'
import { useCountry } from '@/lib/country'
import { dateTime } from '@/lib/format'
import {
  FIELD_TYPES,
  TARGET_LABELS,
  isValidKey,
  keyFromLabel,
  localProblems,
  moveField,
  newField,
  targetsFor,
} from '@/lib/leadFormDesign'
import { useAsync } from '@/lib/useAsync'
import { useUnsavedChanges } from '@/lib/useUnsavedChanges'
import { CodemaxBrand } from '@/components/Brand'
import { LeaveGuard, useLeaveGuard } from '@/components/LeaveGuard'
import { PicklistField } from '@/components/PicklistField'
import { VipAssignmentFields } from '@/components/VipAssignment'
import { PageHeader, Tabs } from '@/components/ui/page'
import { Badge, Busy, Button, Card, CardHeader, EmptyState, ErrorState, Loading, cx } from '@/components/ui/primitives'
import { Dialog } from '@/components/ui/dialog'
import { Field, FormGrid, Input, SaveActions, Select, Switch, Textarea } from '@/components/ui/form'
import { Table, Td, Th, Tr } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type {
  LeadAssignee,
  LeadFieldTarget,
  LeadFormContent,
  LeadFormDetail,
  LeadFormField,
  LeadFormListItem,
  LeadFormSettings,
  LeadFormStatus,
  LeadFormSubmissionItem,
  LeadInboundStatus,
  Paged,
  SalesTeamListItem,
} from '@/types/api'

/**
 * Contact forms: built and published by marketing, without a developer.
 *
 * <p>
 * A form is a list of questions, a few settings and a publish button, deliberately. Publishing
 * freezes the questions into a numbered version and every lead points at the version it
 * answered, so rewording a question next month never changes what last month's answers meant.
 * The settings beside them - who a lead goes to, what the person is told - are live, and decide
 * what happens to the next lead only.
 * </p>
 */

const STATUS_TONE: Record<LeadFormStatus, 'neutral' | 'positive' | 'warning'> = {
  Draft: 'warning',
  Published: 'positive',
  Closed: 'neutral',
}

export function LeadForms() {
  const { current } = useCountry()
  const navigate = useNavigate()
  const toast = useToast()
  const [creating, setCreating] = useState(false)

  const list = useAsync<LeadFormListItem[]>(
    (signal) => api.get<LeadFormListItem[]>('/lead-forms', { countryId: current?.countryId }, signal),
    [current?.countryId],
  )

  const rows = list.data ?? []

  return (
    <>
      <PageHeader
        eyebrow={current?.name}
        title="Contact forms"
        hint="Forms for the website, an ad's landing page or a QR code at a booth. Every lead they produce lands in this country's inbox."
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            New form
          </Button>
        }
      />

      {list.error && <ErrorState title={list.error.title} detail={list.error.detail} onRetry={list.reload} />}

      <Busy busy={list.loading}>
        {list.data && (
          <Card>
            {rows.length === 0 ? (
              <EmptyState
                title="No forms yet"
                hint="A new form starts with name, company, email, phone, a message and the consent question. Change what you like, then publish it."
                action={
                  <Button size="sm" onClick={() => setCreating(true)}>
                    Build the first one
                  </Button>
                }
              />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Form</Th>
                    <Th>Status</Th>
                    <Th numeric>Leads</Th>
                    <Th numeric>Today</Th>
                    <Th>Published</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((form) => (
                    <Tr key={form.id} onClick={() => navigate(`/lead-forms/${form.id}`)}>
                      <Td>
                        <span className="font-medium">{form.name}</span>
                        <span className="block font-mono text-[11.5px] text-muted">/f/{form.slug}</span>
                      </Td>
                      <Td>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge tone={STATUS_TONE[form.status]}>
                            {form.status}
                            {form.publishedVersionNo !== null && form.status !== 'Draft' && ` v${form.publishedVersionNo}`}
                          </Badge>
                          {form.hasUnpublishedChanges && form.publishedVersionNo !== null && (
                            <Badge tone="warning">Unpublished changes</Badge>
                          )}
                          {form.capReachedToday && <Badge tone="critical">Closed for today</Badge>}
                        </div>
                      </Td>
                      <Td numeric>{form.leadCount}</Td>
                      <Td numeric>
                        {form.submissionsToday}
                        <span className="text-faint"> / {form.dailyCap}</span>
                      </Td>
                      <Td className="text-muted">{form.publishedAt ? dateTime(form.publishedAt) : 'Never'}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        )}
      </Busy>

      <NewFormDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id, name) => {
          setCreating(false)
          toast.show(`${name} created`, 'success')
          navigate(`/lead-forms/${id}`)
        }}
      />
    </>
  )
}

/**
 * A form is built for one country and stays there: every lead it produces is in that country,
 * and the country decides the team, the owner and every list the lead appears in.
 */
function NewFormDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (id: string, name: string) => void
}) {
  const toast = useToast()
  const { current, available } = useCountry()
  const [countryId, setCountryId] = useState('')
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setCountryId(current?.countryId ?? '')
      setName('')
    }
  }, [open, current?.countryId])

  async function create() {
    setSaving(true)
    try {
      const created = await api.post<{ id: string }>('/lead-forms', { countryId, name, slug: null })
      onCreated(created.id, name)
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not create the form', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="New contact form"
      description="It starts with the usual questions and a consent line, as a draft nobody can see."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={saving} disabled={!name.trim() || !countryId} onClick={() => void create()}>
            Create form
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Name" required hint="What staff call it - never shown to the public. For example RMS enquiry.">
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label="Country" required hint="Fixed for good once the form is published.">
          {(id) => (
            <Select id={id} value={countryId} onChange={(e) => setCountryId(e.target.value)}>
              {available.map((country) => (
                <option key={country.countryId} value={country.countryId}>
                  {country.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
    </Dialog>
  )
}

/** Everything the designer edits, as one object, so one comparison says what is unsaved. */
interface FormEdit {
  name: string
  slug: string
  content: LeadFormContent
  settings: LeadFormSettings
}

const EDITED = ['name', 'slug', 'content', 'settings'] as const

export function LeadFormDetailPage() {
  const { id } = useParams()
  return id ? <Designer formId={id} /> : <ErrorState title="No form" detail="That link is missing a form id." />
}

type DesignerTab = 'fields' | 'settings' | 'embed' | 'submissions'

function Designer({ formId }: { formId: string }) {
  const navigate = useNavigate()
  const toast = useToast()
  const [tab, setTab] = useState<DesignerTab>('fields')
  const [edit, setEdit] = useState<Partial<FormEdit>>({})
  const [saving, setSaving] = useState(false)
  const [busy, setBusy] = useState(false)
  const [deleting, setDeleting] = useState(false)

  /** Keys present when the form was loaded. Those are what answers are stored under, so they stay put. */
  const [loadedKeys, setLoadedKeys] = useState<string[]>([])

  const detail = useAsync<LeadFormDetail>(
    (signal) =>
      api.get<LeadFormDetail>(`/lead-forms/${formId}`, undefined, signal).then((loaded) => {
        setEdit(toEdit(loaded))
        setLoadedKeys(loaded.content.fields.map((field) => field.key))
        return loaded
      }),
    [formId],
  )

  const baseline = useMemo(() => (detail.data ? (toEdit(detail.data) as FormEdit) : undefined), [detail.data])
  const apply = useCallback((values: Partial<FormEdit>) => setEdit(values), [])
  const changes = useUnsavedChanges<FormEdit>(edit, baseline, apply, EDITED)
  const guard = useLeaveGuard(changes)

  async function save(): Promise<boolean> {
    if (!detail.data || !edit.content || !edit.settings) return false

    setSaving(true)
    try {
      await api.put(`/lead-forms/${formId}`, {
        name: edit.name,

        // The address is fixed once the form has been public; sending it would only be refused.
        slug: detail.data.publishedVersionNo === null ? edit.slug : null,
        content: edit.content,
        settings: edit.settings,
      })
      changes.accept()
      toast.show('Form saved', 'success')
      detail.reload()
      return true
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not save', 'error')
      return false
    } finally {
      setSaving(false)
    }
  }

  async function publish() {
    // The server publishes what is saved, so an unsaved edit is saved first rather than
    // left out of the version the public is about to see.
    if (changes.isDirty && !(await save())) return

    setBusy(true)
    try {
      await api.post(`/lead-forms/${formId}/publish`)
      toast.show('Published', 'success')
      detail.reload()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not publish', 'error')
    } finally {
      setBusy(false)
    }
  }

  async function takeOffline() {
    setBusy(true)
    try {
      await api.post(`/lead-forms/${formId}/close`)
      toast.show('Taken offline. The address now says the form is not available.', 'success')
      detail.reload()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not take it offline', 'error')
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    setBusy(true)
    try {
      await api.del(`/lead-forms/${formId}`)
      toast.show('Form deleted', 'success')
      guard.bypass(() => navigate('/lead-forms', { replace: true }))
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not delete the form', 'error')
      setBusy(false)
    }
  }

  if (detail.loading && !detail.data) return <Loading />
  if (detail.error) return <ErrorState title={detail.error.title} detail={detail.error.detail} onRetry={detail.reload} />
  if (!detail.data || !edit.content || !edit.settings) return null

  const form = detail.data
  const content = edit.content
  const settings = edit.settings

  // The server's list describes the saved draft. While there are unsaved edits, the rules the
  // designer can check itself are the better answer to "what is still wrong".
  const problems = changes.isDirty ? localProblems(content) : form.problemsBeforePublishing
  const neverPublished = form.publishedVersionNo === null

  return (
    <>
      <PageHeader
        eyebrow={`Contact form · ${form.countryIsoCode}`}
        title={edit.name || form.name}
        back={{ label: 'Back to contact forms', onClick: () => navigate('/lead-forms') }}
        actions={
          <>
            {form.canDelete && (
              <Button size="sm" variant="danger" disabled={busy} onClick={() => setDeleting(true)}>
                Delete
              </Button>
            )}
            {form.canPublish && form.status === 'Published' && (
              <Button size="sm" disabled={busy} onClick={() => void takeOffline()}>
                Take offline
              </Button>
            )}
            {form.canPublish && (
              <Button
                size="sm"
                variant="primary"
                loading={busy}
                disabled={problems.length > 0 || (!changes.isDirty && form.status === 'Published' && !form.hasUnpublishedChanges)}
                onClick={() => void publish()}
              >
                {form.status === 'Closed' && !form.hasUnpublishedChanges && !changes.isDirty
                  ? 'Publish again'
                  : `Publish v${neverPublished ? 1 : (form.publishedVersionNo ?? 0) + (form.hasUnpublishedChanges || changes.isDirty ? 1 : 0)}`}
              </Button>
            )}
          </>
        }
      />

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Badge tone={STATUS_TONE[form.status]}>
          {form.status}
          {form.publishedVersionNo !== null && form.status !== 'Draft' && ` v${form.publishedVersionNo}`}
        </Badge>
        {(form.hasUnpublishedChanges || changes.isDirty) && !neverPublished && (
          <Badge tone="warning">{changes.isDirty ? 'Unsaved changes' : 'Unpublished changes'}</Badge>
        )}
        <span className="font-mono text-[12px] text-muted">
          {window.location.origin}/f/{edit.slug ?? form.slug}
        </span>
      </div>

      {problems.length > 0 && (
        <div className="mb-5 rounded-md border border-warn/30 bg-warn-wash px-4 py-3 text-[13px]" role="status">
          <p className="font-medium text-warn">Before this can go live</p>
          <ul className="mt-1 list-disc pl-5 text-ink-2">
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      )}

      <Tabs<DesignerTab>
        active={tab}
        onSelect={setTab}
        tabs={[
          { id: 'fields', label: 'Fields', count: content.fields.length },
          { id: 'settings', label: 'Settings' },
          { id: 'embed', label: 'Embed' },
          { id: 'submissions', label: 'Submissions' },
        ]}
      />

      {tab === 'fields' && (
        <FieldsTab
          name={edit.name ?? ''}
          slug={edit.slug ?? ''}
          slugEditable={neverPublished}
          content={content}
          loadedKeys={loadedKeys}
          neverPublished={neverPublished}
          onName={(name) => setEdit((current) => ({ ...current, name }))}
          onSlug={(slug) => setEdit((current) => ({ ...current, slug }))}
          onContent={(next) => setEdit((current) => ({ ...current, content: next }))}
        />
      )}

      {tab === 'settings' && (
        <SettingsTab
          form={form}
          settings={settings}
          onChange={(next) => setEdit((current) => ({ ...current, settings: next }))}
        />
      )}

      {tab === 'embed' && <EmbedTab form={form} slug={edit.slug ?? form.slug} />}

      {tab === 'submissions' && <SubmissionsTab formId={formId} />}

      {(tab === 'fields' || tab === 'settings') && (
        <Card className="mt-5">
          <SaveActions
            count={changes.fields.length}
            saving={saving}
            saveLabel="Save draft"
            canSave={Boolean(edit.name?.trim())}
            onSave={() => void save()}
            onDiscard={changes.discard}
          >
            <span className="mr-auto text-[12.5px] text-muted">
              Saving changes the draft. The public sees nothing new until it is published.
            </span>
          </SaveActions>
        </Card>
      )}

      <LeaveGuard guard={guard} />

      <Dialog
        open={deleting}
        title={`Delete ${form.name}?`}
        onClose={() => setDeleting(false)}
        footer={
          <>
            <Button size="sm" onClick={() => setDeleting(false)}>
              Keep it
            </Button>
            <Button size="sm" variant="danger" loading={busy} onClick={() => void remove()}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-ink-2">
          Nobody has filled this form in, so nothing points at it. Once somebody has, it is the
          record of what they were asked and can only be taken offline.
        </p>
      </Dialog>
    </>
  )
}

function toEdit(form: LeadFormDetail): FormEdit {
  return { name: form.name, slug: form.slug, content: form.content, settings: form.settings }
}

/**
 * The questions: a palette to add from, the form as the public will see it, and the selected
 * question's properties.
 */
function FieldsTab({
  name,
  slug,
  slugEditable,
  content,
  loadedKeys,
  neverPublished,
  onName,
  onSlug,
  onContent,
}: {
  name: string
  slug: string
  slugEditable: boolean
  content: LeadFormContent
  loadedKeys: readonly string[]
  neverPublished: boolean
  onName: (name: string) => void
  onSlug: (slug: string) => void
  onContent: (content: LeadFormContent) => void
}) {
  const [selected, setSelected] = useState<number | undefined>(content.fields.length > 0 ? 0 : undefined)

  /** Keys somebody typed by hand, which a later label change must not overwrite. */
  const [manualKeys, setManualKeys] = useState<string[]>([])

  const field = selected === undefined ? undefined : content.fields[selected]

  function setFields(fields: LeadFormField[]) {
    onContent({ ...content, fields })
  }

  function add(type: LeadFormField['type']) {
    const created = newField(type, content.fields)
    setFields([...content.fields, created])
    setSelected(content.fields.length)
  }

  function update(index: number, patch: Partial<LeadFormField>) {
    setFields(content.fields.map((existing, i) => (i === index ? { ...existing, ...patch } : existing)))
  }

  function remove(index: number) {
    setFields(content.fields.filter((_, i) => i !== index))
    setSelected(content.fields.length > 1 ? Math.max(0, index - 1) : undefined)
  }

  function move(index: number, by: -1 | 1) {
    setFields(moveField(content.fields, index, by))
    setSelected(Math.min(Math.max(0, index + by), content.fields.length - 1))
  }

  // A key is fixed once the public has answered under it. A question added since the last
  // load has never been answered, so it may still be renamed.
  const keyEditable = field !== undefined && (neverPublished || !loadedKeys.includes(field.key))

  function relabel(index: number, label: string) {
    const current = content.fields[index]
    if (!current) return

    const derive = keyEditable && !manualKeys.includes(current.key) && current.type !== 'Consent'
    const others = content.fields.filter((_, i) => i !== index).map((other) => other.key)

    update(index, derive ? { label, key: keyFromLabel(label, others) } : { label })
  }

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader title="The form" />
        <FormGrid>
          <Field label="Name" required hint="Staff only.">
            {(id) => <Input id={id} value={name} onChange={(e) => onName(e.target.value)} />}
          </Field>
          <Field
            label="Address"
            hint={slugEditable ? 'Lower-case letters, digits and hyphens.' : 'Fixed - it may already be printed or advertised.'}
          >
            {(id) => (
              <Input
                id={id}
                disabled={!slugEditable}
                className="font-mono"
                value={slug}
                onChange={(e) => onSlug(e.target.value.toLowerCase())}
              />
            )}
          </Field>
          <Field label="Title" required hint="The heading the public sees.">
            {(id) => <Input id={id} value={content.title} onChange={(e) => onContent({ ...content, title: e.target.value })} />}
          </Field>
          <Field label="Button label">
            {(id) => (
              <Input
                id={id}
                placeholder="Send"
                value={content.submitLabel ?? ''}
                onChange={(e) => onContent({ ...content, submitLabel: e.target.value || null })}
              />
            )}
          </Field>
          <div className="sm:col-span-2">
            <Field label="Introduction">
              {(id) => (
                <Textarea
                  id={id}
                  rows={2}
                  maxLength={1000}
                  value={content.intro ?? ''}
                  onChange={(e) => onContent({ ...content, intro: e.target.value || null })}
                />
              )}
            </Field>
          </div>
        </FormGrid>
      </Card>

      <Card className="grid overflow-hidden lg:grid-cols-[170px_minmax(0,1fr)_300px]">
        <div className="border-b border-line-soft p-4 lg:border-b-0 lg:border-r">
          <p className="mb-2.5 font-mono text-[10.5px] uppercase tracking-[0.13em] text-muted">Add a question</p>
          <div className="grid grid-cols-2 gap-1.5 lg:grid-cols-1">
            {FIELD_TYPES.map((entry) => (
              <button
                key={entry.type}
                type="button"
                onClick={() => add(entry.type)}
                disabled={entry.type === 'Consent' && content.fields.some((existing) => existing.type === 'Consent')}
                className="flex items-center gap-2 rounded-md border border-line bg-surface-2 px-2.5 py-1.5 text-left text-[13px] text-ink-2 transition-colors hover:border-accent hover:text-ink disabled:opacity-40"
              >
                <span aria-hidden className="w-5 font-mono text-[11px] font-semibold text-accent">
                  {entry.glyph}
                </span>
                {entry.label}
              </button>
            ))}
          </div>
          <p className="mt-3 text-[11.5px] text-muted">Click a question in the form to edit it.</p>
        </div>

        <div className="bg-sunken p-4 sm:p-6">
          <FormPreview
            content={content}
            selected={selected}
            onSelect={setSelected}
            onMove={move}
            onRemove={remove}
          />
        </div>

        <div className="border-t border-line-soft p-4 lg:border-l lg:border-t-0">
          {field && selected !== undefined ? (
            <FieldProperties
              field={field}
              keyEditable={keyEditable}
              takenTargets={content.fields.filter((_, i) => i !== selected).map((other) => other.target ?? null)}
              takenKeys={content.fields.filter((_, i) => i !== selected).map((other) => other.key)}
              onLabel={(label) => relabel(selected, label)}
              onKey={(key) => {
                setManualKeys((keys) => [...keys, key])
                update(selected, { key })
              }}
              onChange={(patch) => update(selected, patch)}
            />
          ) : (
            <p className="text-[13px] text-muted">No question selected.</p>
          )}
        </div>
      </Card>
    </div>
  )
}

/**
 * The form as the public will see it: Codemax-branded, because a prospective customer is the
 * one reading it. The QuoteHero mark never appears on anything outside Codemax sees.
 */
function FormPreview({
  content,
  selected,
  onSelect,
  onMove,
  onRemove,
}: {
  content: LeadFormContent
  selected: number | undefined
  onSelect: (index: number) => void
  onMove: (index: number, by: -1 | 1) => void
  onRemove: (index: number) => void
}) {
  return (
    <div className="mx-auto max-w-[540px] rounded-lg border border-line bg-surface p-5 shadow-card">
      <CodemaxBrand className="h-6" />
      <h3 className="mt-4 text-lg font-semibold tracking-[-0.01em]">{content.title || 'Untitled form'}</h3>
      {content.intro && <p className="mt-1 text-[13px] text-muted">{content.intro}</p>}

      {content.fields.length === 0 ? (
        <p className="mt-5 rounded-md border border-dashed border-line px-4 py-6 text-center text-[13px] text-muted">
          Add a question from the palette.
        </p>
      ) : (
        <div className="mt-4 grid gap-x-3 gap-y-1 sm:grid-cols-2">
          {content.fields.map((field, index) => (
            <div
              key={`${field.key}-${index}`}
              role="button"
              tabIndex={0}
              aria-pressed={selected === index}
              onClick={() => onSelect(index)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onSelect(index)
                }
              }}
              className={cx(
                'group relative cursor-pointer rounded-md border px-2 py-2',
                field.width === 'half' ? 'sm:col-span-1' : 'sm:col-span-2',
                selected === index ? 'border-accent bg-accent-wash' : 'border-transparent hover:border-line',
              )}
            >
              <PreviewControl field={field} />

              <div
                className={cx(
                  'absolute right-1 top-1 flex gap-0.5',
                  selected === index ? 'flex' : 'hidden group-hover:flex group-focus-within:flex',
                )}
              >
                <TinyButton label="Move up" onClick={() => onMove(index, -1)} disabled={index === 0}>
                  {'↑'}
                </TinyButton>
                <TinyButton label="Move down" onClick={() => onMove(index, 1)} disabled={index === content.fields.length - 1}>
                  {'↓'}
                </TinyButton>
                <TinyButton label="Remove" onClick={() => onRemove(index)}>
                  {'×'}
                </TinyButton>
              </div>
            </div>
          ))}
        </div>
      )}

      <span className="mt-4 inline-block rounded-md bg-accent px-4 py-2 text-[13px] font-medium text-white">
        {content.submitLabel || 'Send'}
      </span>
    </div>
  )
}

function TinyButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={(event) => {
        // Inside the selectable question, so the click must not also select it.
        event.stopPropagation()
        onClick()
      }}
      className="grid size-6 place-items-center rounded border border-line bg-surface text-[12px] text-ink-2 hover:text-ink disabled:opacity-30"
    >
      {children}
    </button>
  )
}

/** A question drawn the way the public page draws it, inert. */
function PreviewControl({ field }: { field: LeadFormField }) {
  const label = (
    <span className="mb-1 block text-[12.5px] font-medium text-ink-2">
      {field.label || 'Untitled question'}
      {field.required && <span className="ml-0.5 text-crit">*</span>}
    </span>
  )
  const box = 'block w-full rounded-md border border-line bg-surface px-2.5 text-[12.5px] text-faint'

  switch (field.type) {
    case 'Consent':
    case 'Checkbox':
      return (
        <span className="flex items-start gap-2 text-[12px] leading-snug text-ink-2">
          <span aria-hidden className="mt-0.5 size-3.5 shrink-0 rounded-sm border border-line bg-surface" />
          <span>
            {field.label || 'Untitled question'}
            {field.required && <span className="ml-0.5 text-crit">*</span>}
          </span>
        </span>
      )
    case 'LongText':
      return (
        <>
          {label}
          <span className={cx(box, 'h-14 py-1.5')}>{field.placeholder}</span>
        </>
      )
    case 'Choice':
      return (
        <>
          {label}
          {field.display === 'buttons' ? (
            <span className="flex flex-wrap gap-1.5">
              {(field.options ?? []).map((option) => (
                <span key={option.value} className="rounded-md border border-line bg-surface px-2.5 py-1 text-[12px] text-ink-2">
                  {option.label}
                </span>
              ))}
            </span>
          ) : (
            <span className={cx(box, 'flex h-8 items-center justify-between')}>
              {field.placeholder || 'Choose…'}
              <span aria-hidden>{'▾'}</span>
            </span>
          )}
        </>
      )
    default:
      return (
        <>
          {label}
          <span className={cx(box, 'flex h-8 items-center')}>{field.placeholder}</span>
        </>
      )
  }
}

function FieldProperties({
  field,
  keyEditable,
  takenTargets,
  takenKeys,
  onLabel,
  onKey,
  onChange,
}: {
  field: LeadFormField
  keyEditable: boolean
  takenTargets: readonly (LeadFieldTarget | null)[]
  takenKeys: readonly string[]
  onLabel: (label: string) => void
  onKey: (key: string) => void
  onChange: (patch: Partial<LeadFormField>) => void
}) {
  const targets = targetsFor(field.type)
  const typeLabel = FIELD_TYPES.find((entry) => entry.type === field.type)?.label ?? field.type
  const keyProblem = !isValidKey(field.key)
    ? 'Lower-case letters, digits and underscores, starting with a letter.'
    : takenKeys.includes(field.key)
      ? 'Another question already uses this key.'
      : undefined

  return (
    <div className="flex flex-col gap-3.5">
      <p className="font-mono text-[10.5px] uppercase tracking-[0.13em] text-muted">Question · {typeLabel}</p>

      <Field label={field.type === 'Consent' ? 'Wording they agree to' : 'Label'} required>
        {(id) =>
          field.type === 'Consent' ? (
            <Textarea id={id} rows={4} maxLength={1000} value={field.label} onChange={(e) => onLabel(e.target.value)} />
          ) : (
            <Input id={id} maxLength={300} value={field.label} onChange={(e) => onLabel(e.target.value)} />
          )
        }
      </Field>

      {field.type === 'Consent' && (
        <p className="-mt-2 text-[12px] text-muted">
          Stored on every lead exactly as written here, with the time they agreed.
        </p>
      )}

      {targets.length > 0 && (
        <Field label="Saves to" hint="Anything not saved to a column is kept as a custom answer.">
          {(id) => (
            <Select
              id={id}
              value={field.target ?? ''}
              onChange={(e) => onChange({ target: (e.target.value || null) as LeadFieldTarget | null })}
            >
              <option value="">Custom answer</option>
              {targets.map((target) => (
                <option key={target} value={target} disabled={takenTargets.includes(target)}>
                  {TARGET_LABELS[target]}
                  {takenTargets.includes(target) ? ' (used by another question)' : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}

      <Field
        label="Key"
        error={keyProblem}
        hint={keyEditable ? 'What the answer is stored under.' : 'Fixed - answers are already stored under it.'}
      >
        {(id) => (
          <Input id={id} className="font-mono" disabled={!keyEditable} value={field.key} onChange={(e) => onKey(e.target.value)} />
        )}
      </Field>

      {field.type === 'Choice' && (
        <ChoiceOptions field={field} onChange={onChange} />
      )}

      {field.type !== 'Consent' && field.type !== 'Checkbox' && (
        <Field label="Placeholder">
          {(id) => (
            <Input id={id} value={field.placeholder ?? ''} onChange={(e) => onChange({ placeholder: e.target.value || null })} />
          )}
        </Field>
      )}

      {field.type !== 'Consent' && (
        <Field label="Help text">
          {(id) => <Input id={id} value={field.helpText ?? ''} onChange={(e) => onChange({ helpText: e.target.value || null })} />}
        </Field>
      )}

      <Segmented
        label="Width"
        value={field.width === 'half' ? 'half' : 'full'}
        options={[
          { value: 'half', label: 'Half' },
          { value: 'full', label: 'Full' },
        ]}
        onChange={(width) => onChange({ width })}
      />

      <Switch
        label="Required"
        hint={field.type === 'Consent' ? 'A consent question is always required.' : undefined}
        checked={field.required}
        disabled={field.type === 'Consent'}
        onChange={(required) => onChange({ required })}
      />
    </div>
  )
}

function ChoiceOptions({ field, onChange }: { field: LeadFormField; onChange: (patch: Partial<LeadFormField>) => void }) {
  const options = field.options ?? []

  function setOptions(next: { value: string; label: string }[]) {
    onChange({ options: next })
  }

  return (
    <>
      <Segmented
        label="Shown as"
        value={field.display === 'buttons' ? 'buttons' : 'dropdown'}
        options={[
          { value: 'dropdown', label: 'Dropdown' },
          { value: 'buttons', label: 'Buttons' },
        ]}
        onChange={(display) => onChange({ display })}
      />

      <div>
        <p className="mb-1.5 text-[13px] font-medium text-ink-2">Options</p>
        <div className="flex flex-col gap-1.5">
          {options.map((option, index) => (
            <div key={index} className="flex gap-1.5">
              <Input
                aria-label={`Option ${index + 1}`}
                value={option.label}
                onChange={(e) =>
                  setOptions(
                    options.map((existing, i) =>
                      i === index
                        ? {
                            label: e.target.value,
                            // The value is what is posted; it follows the label until the form
                            // is live, and the label shown to the public is what is stored.
                            value: keyFromLabel(e.target.value, options.filter((_, j) => j !== index).map((o) => o.value)),
                          }
                        : existing,
                    ),
                  )
                }
              />
              <Button size="sm" aria-label="Remove option" onClick={() => setOptions(options.filter((_, i) => i !== index))}>
                {'×'}
              </Button>
            </div>
          ))}
        </div>
        <Button
          size="sm"
          className="mt-2"
          disabled={options.length >= 50}
          onClick={() => {
            const label = `Option ${options.length + 1}`
            setOptions([...options, { label, value: keyFromLabel(label, options.map((o) => o.value)) }])
          }}
        >
          Add option
        </Button>
      </div>
    </>
  )
}

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: T
  options: { value: T; label: string }[]
  onChange: (value: T) => void
}) {
  return (
    <div>
      <p className="mb-1.5 text-[13px] font-medium text-ink-2">{label}</p>
      <div role="radiogroup" aria-label={label} className="flex overflow-hidden rounded-md border border-line">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={value === option.value}
            onClick={() => onChange(option.value)}
            className={cx(
              'flex-1 py-1.5 text-[12.5px] transition-colors',
              value === option.value ? 'bg-accent-wash font-semibold text-accent' : 'text-muted hover:text-ink',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * What happens to a lead once it arrives: who gets it, what the person is told, and where the
 * form may live.
 */
function SettingsTab({
  form,
  settings,
  onChange,
}: {
  form: LeadFormDetail
  settings: LeadFormSettings
  onChange: (settings: LeadFormSettings) => void
}) {
  function set<K extends keyof LeadFormSettings>(key: K, value: LeadFormSettings[K]) {
    onChange({ ...settings, [key]: value })
  }

  const people = useAsync<LeadAssignee[]>(
    (signal) =>
      settings.assignmentMode === 'FixedUser'
        ? api.get<LeadAssignee[]>('/leads/assignees', { countryId: form.countryId }, signal)
        : Promise.resolve([]),
    [settings.assignmentMode, form.countryId],
  )

  const teams = useAsync<SalesTeamListItem[]>(
    (signal) =>
      settings.assignmentMode === 'RoundRobin'
        ? api
            .get<Paged<SalesTeamListItem>>('/sales-teams', { pageSize: 200, countryId: form.countryId }, signal)
            .then((paged) => paged.items)
        : Promise.resolve([]),
    [settings.assignmentMode, form.countryId],
  )

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader title="Who gets the leads" hint={`Every lead from this form is in ${form.countryIsoCode}.`} />
        <FormGrid>
          <PicklistField
            label="Source of lead"
            hint="Stamped on every lead, and on the customer it becomes."
            list="LeadSource"
            countryId={form.countryId}
            value={settings.leadSourceId}
            onChange={(next) => set('leadSourceId', next)}
          />
          <Field label="Assign to">
            {(id) => (
              <Select
                id={id}
                value={settings.assignmentMode}
                onChange={(e) => set('assignmentMode', e.target.value as LeadFormSettings['assignmentMode'])}
              >
                <option value="Unassigned">Nobody - the country's unassigned queue</option>
                <option value="FixedUser">One person</option>
                <option value="RoundRobin">A sales team, in turn</option>
              </Select>
            )}
          </Field>

          {settings.assignmentMode === 'FixedUser' && (
            <Field label="Person" required hint="Only people who can open leads in this country.">
              {(id) => (
                <Select
                  id={id}
                  disabled={people.loading}
                  value={settings.assignedUserId ?? ''}
                  onChange={(e) => set('assignedUserId', e.target.value || null)}
                >
                  <option value="">Choose somebody</option>
                  {(people.data ?? []).map((person) => (
                    <option key={person.userId} value={person.userId}>
                      {person.displayName}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}

          {settings.assignmentMode === 'RoundRobin' && (
            <Field
              label="Team"
              required
              hint="Each lead goes to the next member who can open leads here. Nobody eligible means the queue, never a lost lead."
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={teams.loading}
                  value={settings.salesTeamId ?? ''}
                  onChange={(e) => set('salesTeamId', e.target.value || null)}
                >
                  <option value="">Choose a team</option>
                  {(teams.data ?? [])
                    .filter((team) => team.isActive || team.id === settings.salesTeamId)
                    .map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                </Select>
              )}
            </Field>
          )}

          <VipAssignmentFields
            countryId={form.countryId}
            rule={{
              mode: settings.vipAssignmentMode ?? null,
              userId: settings.vipAssignedUserId ?? null,
              teamId: settings.vipSalesTeamId ?? null,
            }}
            onChange={(rule) =>
              onChange({
                ...settings,
                vipAssignmentMode: rule.mode,
                vipAssignedUserId: rule.userId,
                vipSalesTeamId: rule.teamId,
              })
            }
          />
        </FormGrid>
      </Card>

      <Card>
        <CardHeader title="What the person is told" />
        <FormGrid>
          <div className="sm:col-span-2">
            <Field label="Thank-you message" hint="Shown on the page once they send it.">
              {(id) => (
                <Textarea
                  id={id}
                  rows={2}
                  maxLength={1000}
                  value={settings.successMessage ?? ''}
                  onChange={(e) => set('successMessage', e.target.value || null)}
                />
              )}
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Or send them to a page" hint="A full https:// address. Leave empty to show the message instead.">
              {(id) => (
                <Input
                  id={id}
                  type="url"
                  placeholder="https://"
                  value={settings.redirectUrl ?? ''}
                  onChange={(e) => set('redirectUrl', e.target.value || null)}
                />
              )}
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Switch
              label="Email them a receipt"
              hint="From Codemax, to the address they gave. The form must ask for an email address."
              checked={settings.acknowledgeByEmail}
              onChange={(next) => set('acknowledgeByEmail', next)}
            />
          </div>
          {settings.acknowledgeByEmail && (
            <>
              <div className="sm:col-span-2">
                <Field label="Subject">
                  {(id) => (
                    <Input
                      id={id}
                      maxLength={200}
                      placeholder="Thank you for your enquiry"
                      value={settings.acknowledgementSubject ?? ''}
                      onChange={(e) => set('acknowledgementSubject', e.target.value || null)}
                    />
                  )}
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Message" hint="Plain text. Write {{Name}} where their name should go. A blank line starts a new paragraph.">
                  {(id) => (
                    <Textarea
                      id={id}
                      rows={6}
                      maxLength={4000}
                      value={settings.acknowledgementBody ?? ''}
                      onChange={(e) => set('acknowledgementBody', e.target.value || null)}
                    />
                  )}
                </Field>
              </div>
            </>
          )}
        </FormGrid>
      </Card>

      <Card>
        <CardHeader title="Keeping it safe" />
        <FormGrid>
          <Field
            label="Sites it may be embedded on"
            hint="One per line, such as codemax.my. Empty means anywhere. The form's own page always works."
          >
            {(id) => (
              <Textarea
                id={id}
                rows={3}
                className="font-mono"
                value={settings.allowedHosts ?? ''}
                onChange={(e) => set('allowedHosts', e.target.value || null)}
              />
            )}
          </Field>
          <Field label="Daily limit" hint="Past this many in a day the form closes until tomorrow.">
            {(id) => (
              <Input
                id={id}
                type="number"
                min={1}
                max={5000}
                value={settings.dailyCap}
                onChange={(e) => set('dailyCap', Number(e.target.value) || 0)}
              />
            )}
          </Field>
          <div className="sm:col-span-2">
            <Switch
              label="Ask for a captcha"
              hint={
                form.captchaAvailable
                  ? 'For a form that is being attacked. Real people get one more step.'
                  : 'Captcha keys are not configured on this server, so a form that asked for one would refuse everybody.'
              }
              checked={settings.requireCaptcha}
              disabled={!form.captchaAvailable && !settings.requireCaptcha}
              onChange={(next) => set('requireCaptcha', next)}
            />
          </div>
        </FormGrid>
      </Card>
    </div>
  )
}

/** The three ways to put the form somewhere, and every version the public has been shown. */
function EmbedTab({ form, slug }: { form: LeadFormDetail; slug: string }) {
  const origin = window.location.origin
  const live = form.status === 'Published'

  return (
    <div className="flex flex-col gap-5">
      {!live && (
        <p className="rounded-md border border-warn/30 bg-warn-wash px-4 py-2.5 text-[13px] text-warn">
          {form.status === 'Draft'
            ? 'Not published yet. These snippets show "not available" until it is.'
            : 'Offline. These snippets show "not available" until it is published again.'}
        </p>
      )}

      <Card>
        <CardHeader title="Put it on a website" hint="All three show the same form and send to the same inbox." />
        <div className="flex flex-col gap-5 px-5 pb-5">
          <Snippet
            label="Link"
            hint="For an ad's landing page, a QR code, an email signature."
            code={`${origin}/f/${slug}`}
          />
          <Snippet
            label="Embed"
            hint="Paste into WordPress, Wix or any page. It sizes itself as the form grows."
            code={`<script src="${origin}/embed.js" data-form="${slug}" async></script>`}
          />
          <Snippet
            label="Iframe"
            hint="For a site builder that refuses scripts."
            code={`<iframe src="${origin}/f/${slug}?embed=1" style="width:100%;border:0" height="640" title="Enquiry"></iframe>`}
          />
        </div>
      </Card>

      <Card>
        <CardHeader title="Versions" hint="Every published shape of the questions. Leads point at the version they answered." />
        {form.versions.length === 0 ? (
          <EmptyState title="Never published" hint="Publishing freezes the questions into version 1." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Version</Th>
                <Th>Published</Th>
                <Th>By</Th>
                <Th>Fingerprint</Th>
              </tr>
            </thead>
            <tbody>
              {form.versions.map((version) => (
                <tr key={version.id}>
                  <Td className="font-medium">v{version.versionNo}</Td>
                  <Td className="text-muted">{dateTime(version.publishedAt)}</Td>
                  <Td className="text-muted">{version.publishedBy ?? '-'}</Td>
                  <Td className="font-mono text-[11.5px] text-faint">{version.sha256.slice(0, 16)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  )
}

function Snippet({ label, hint, code }: { label: string; hint: string; code: string }) {
  const toast = useToast()

  async function copy() {
    try {
      await navigator.clipboard.writeText(code)
      toast.show(`${label} copied`, 'success')
    } catch {
      toast.show('Could not copy - select the text and copy it instead', 'error')
    }
  }

  return (
    <div>
      <div className="mb-1.5 flex items-end justify-between gap-3">
        <div>
          <p className="text-[13px] font-medium text-ink-2">{label}</p>
          <p className="text-[12px] text-muted">{hint}</p>
        </div>
        <Button size="sm" onClick={() => void copy()}>
          Copy
        </Button>
      </div>
      <pre className="overflow-x-auto rounded-md border border-line bg-surface-2 px-3 py-2 font-mono text-[12px] text-ink-2">
        {code}
      </pre>
    </div>
  )
}

const SUBMISSION_LABELS: Record<LeadInboundStatus, { label: string; tone: 'neutral' | 'positive' | 'warning' | 'critical' | 'accent' }> = {
  Received: { label: 'Waiting', tone: 'warning' },
  Processed: { label: 'New lead', tone: 'positive' },
  Merged: { label: 'Added to a lead', tone: 'accent' },
  Unmapped: { label: 'Held', tone: 'warning' },
  Failed: { label: 'Failed', tone: 'critical' },
  RejectedSpam: { label: 'Spam', tone: 'neutral' },
  RejectedCapped: { label: 'Over the limit', tone: 'neutral' },
  Test: { label: 'Test', tone: 'neutral' },
}

/**
 * What reached this form recently, including what was turned away. Never the answers
 * themselves: those belong on the lead, behind the lead's own permission.
 */
function SubmissionsTab({ formId }: { formId: string }) {
  const navigate = useNavigate()
  const list = useAsync<LeadFormSubmissionItem[]>(
    (signal) => api.get<LeadFormSubmissionItem[]>(`/lead-forms/${formId}/submissions`, undefined, signal),
    [formId],
  )

  if (list.error) return <ErrorState title={list.error.title} detail={list.error.detail} onRetry={list.reload} />

  return (
    <Busy busy={list.loading}>
      <Card>
        <CardHeader title="Recent submissions" hint="The last hundred, spam included - it is the evidence somebody is hammering the form." />
        {(list.data ?? []).length === 0 ? (
          <EmptyState title="Nothing yet" hint="Submissions appear here the moment they arrive." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Received</Th>
                <Th>Outcome</Th>
                <Th>Lead</Th>
                <Th>Embedded on</Th>
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((row) => {
                const outcome = SUBMISSION_LABELS[row.status]
                const leadId = row.leadId

                return (
                  <Tr key={row.id} onClick={leadId ? () => navigate(`/leads/${leadId}`) : undefined}>
                    <Td className="text-muted">{dateTime(row.receivedAt)}</Td>
                    <Td>
                      <Badge tone={outcome.tone}>{outcome.label}</Badge>
                      {row.lastError && <span className="block text-[12px] text-crit">{row.lastError}</span>}
                    </Td>
                    <Td>{row.leadName ?? '-'}</Td>
                    <Td className="font-mono text-[12px] text-muted">{row.embeddedOn ?? 'Its own page'}</Td>
                  </Tr>
                )
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </Busy>
  )
}
